import { useMemo, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  Activity,
  Banknote,
  Building2,
  Database,
  Inbox,
  ListChecks,
  LogOut,
  Mail,
  Megaphone,
  Menu,
  ShieldCheck,
  UserCog,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useAuth } from '@/auth/useAuth'
import { cn } from '@/lib/cn'
import { TenantLocaleSync } from '@/components/TenantLocaleSync'

type NavEntry = {
  path: string
  label: string
  icon: LucideIcon
  permission: string | null
}

const NAV: NavEntry[] = [
  { path: '/platform-admin',             label: 'Overview',          icon: Activity,    permission: null },
  { path: '/platform-admin/tenants',     label: 'Customer Tenants',  icon: Building2,   permission: 'admin.tenants.view' },
  { path: '/platform-admin/users',       label: 'Customer Users',    icon: Users,       permission: 'admin.users.view' },
  { path: '/platform-admin/staff',       label: 'Staff',             icon: UserCog,     permission: 'admin.staff.manage' },
  { path: '/platform-admin/invitations', label: 'Staff Invitations', icon: Mail,        permission: 'admin.staff.invite' },
  { path: '/platform-admin/roles',       label: 'Staff Roles',       icon: ShieldCheck, permission: 'admin.staff.roles' },
  { path: '/platform-admin/audit',       label: 'Audit Log',         icon: ListChecks,  permission: 'admin.audit.view' },
  { path: '/platform-admin/contact-messages', label: 'Contact Messages', icon: Inbox,   permission: 'admin.contact_messages.view' },
  { path: '/platform-admin/managed-payouts', label: 'Managed Payouts', icon: Banknote,  permission: 'admin.managed_payouts.view' },
  { path: '/platform-admin/announcements', label: 'Announcements', icon: Megaphone, permission: 'admin.announcements.view' },
  { path: '/platform-admin/tools',       label: 'Database Tools',    icon: Database,    permission: 'admin.tools' },
]

/**
 * Standalone layout for the Bokapsys platform-admin console. Mirrors the
 * legacy platform_admin.html shell (its own sidebar + branding), distinct
 * from the customer AppShell. Light-only by design.
 */
export function PlatformAdminShell() {
  const { user, hasPermission, logout } = useAuth()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)

  const visible = useMemo(
    () => NAV.filter((n) => !n.permission || hasPermission(n.permission)),
    [hasPermission],
  )

  const role = user?.role ?? '-'

  const handleSignOut = async () => {
    await logout()
    navigate('/admin-login', { replace: true })
  }

  return (
    <div className="relative flex h-screen overflow-hidden bg-[#f4f6fb] text-[#1a1a2e]">
      <TenantLocaleSync />
      <span
        aria-hidden
        className="pointer-events-none fixed -right-32 -top-32 z-0 h-[480px] w-[480px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(0,114,206,0.12) 0%, transparent 70%)' }}
      />
      <span
        aria-hidden
        className="pointer-events-none fixed -bottom-24 -left-20 z-0 h-[380px] w-[380px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)' }}
      />

      {mobileOpen ? (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-[260px] flex-col border-r border-slate-200 bg-white transition-transform duration-300 lg:static lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div
          className="border-b px-8 pb-5 pt-6"
          style={{
            background: 'linear-gradient(180deg, #F0F7FF 0%, rgba(255,255,255,0) 100%)',
            borderColor: 'rgba(226,232,240,0.4)',
          }}
        >
          <h1
            className="m-0 text-[22px] font-bold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #0072CE 0%, #7c3aed 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Bokapsys
          </h1>
          <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.8px] text-slate-400">
            Platform Admin
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-5 py-6">
          {visible.map((item) => {
            const Icon = item.icon
            const isIndex = item.path === '/platform-admin'
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={isIndex}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'relative mb-1 flex w-full items-center gap-3 rounded-md px-4 py-3 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-[rgba(0,114,206,0.10)] font-semibold text-[#0072CE]'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-[#0072CE]',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive ? (
                      <span
                        aria-hidden
                        className="absolute inset-y-[15%] left-0 w-[3px] rounded-r bg-[#0072CE]"
                      />
                    ) : null}
                    <Icon className="size-4 shrink-0" aria-hidden />
                    <span className="truncate">{item.label}</span>
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>

        <div
          className="border-t px-6 pb-5 pt-4 text-xs text-slate-500"
          style={{
            borderColor: 'rgba(226,232,240,0.5)',
            background: 'rgba(248,250,252,0.5)',
          }}
        >
          <div className="break-all text-[13px] font-semibold text-[#1a1a2e]">
            {user?.email ?? '-'}
          </div>
          <span
            className="mt-1.5 inline-block rounded-xl border px-2.5 py-[3px] text-[10px] font-bold uppercase tracking-[0.4px]"
            style={{
              background: 'rgba(0,114,206,0.10)',
              color: '#0072CE',
              borderColor: 'rgba(0,114,206,0.20)',
            }}
          >
            {role}
          </span>
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="mt-3.5 flex items-center gap-1.5 text-xs font-medium text-slate-400 transition-colors hover:text-[#0072CE]"
          >
            <LogOut className="size-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <main className="relative z-10 min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-9">
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
          className="mb-3 inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm lg:hidden"
        >
          {mobileOpen ? <X className="size-4" /> : <Menu className="size-4" />}
          Menu
        </button>
        <Outlet />
      </main>
    </div>
  )
}
